import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  AlertCircle,
  Award,
  Ban,
  Calendar,
  Download,
  FileText,
  Search,
  ShieldCheck,
  ShieldOff,
  UploadCloud,
  User,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Modal } from '@/components/ui/Modal'
import { Textarea } from '@/components/ui/textarea'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Paginator } from '@/components/ui/Paginator'
import { CertificateModel } from '@/models/certificate.model'
import type { UserModel } from '@/models/user.model'
import { UserRole } from '@/models/user.model'
import { useCertificates, useRevokeCertificate, useUploadCertificate } from '@/hooks/useCertificates'
import { validateFileUpload } from '@/utils/security'
import { useUsersByIds } from '@/hooks/useUsers'
import { useProgrammesByIds } from '@/hooks/useProgrammesByIds'
import { useCoursesByIds } from '@/hooks/useCourses'
import { useUserList } from '@/hooks/useUserList'
import { cn } from '@/utils/cn'

type CertificateStatusFilter = 'all' | 'valid' | 'revoked' | 'invalid'
type CertificateTypeFilter = 'all' | 'course' | 'programme' | 'diploma'

interface IssueCertificateFormValues {
  userId: string
  title: string
  certificateNumber: string
  replace: boolean
  notify: boolean
}

interface RevokeFormValues {
  reason: string
}

const formatDate = (value: string | null) => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(value))
  } catch {
    return value
  }
}

const resolveStatusBadge = (certificate: CertificateModel) => {
  if (certificate.isRevoked) {
    return { label: 'Revoked', className: 'bg-rose-50 text-rose-700 border border-rose-200' }
  }
  if (certificate.isValid === false) {
    return { label: 'Invalid', className: 'bg-amber-50 text-amber-700 border border-amber-200' }
  }
  return { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' }
}

export function CertificatesPage() {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [typeFilter, setTypeFilter] = useState<CertificateTypeFilter>('all')
  const [statusFilter, setStatusFilter] = useState<CertificateStatusFilter>('all')
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<CertificateModel | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(searchInput.trim().toLowerCase())
      setPage(1)
    }, 300)
    return () => clearTimeout(handler)
  }, [searchInput])

  const { data, isLoading, isFetching, error } = useCertificates({ page, perPage })
  const certificates = data?.data ?? []
  const meta = data?.meta

  const userIds = useMemo(
    () => Array.from(new Set(certificates.map((item) => item.userId).filter(Boolean) as string[])),
    [certificates],
  )
  const programmeIds = useMemo(
    () => Array.from(new Set(certificates.map((item) => item.programmeId).filter(Boolean) as string[])),
    [certificates],
  )
  const courseIds = useMemo(
    () => Array.from(new Set(certificates.map((item) => item.courseId).filter(Boolean) as string[])),
    [certificates],
  )

  const { data: userMap } = useUsersByIds(userIds)
  const { data: programmeMap } = useProgrammesByIds(programmeIds)
  const { data: courseMap } = useCoursesByIds(courseIds)
  const { data: learnersPage, isLoading: isLearnersLoading } = useUserList({
    page: 1,
    perPage: 100,
    role: UserRole.LEARNER,
    isActive: true,
  })
  const learnerOptions = learnersPage?.data ?? []

  const issueForm = useForm<IssueCertificateFormValues>({
    defaultValues: {
      userId: '',
      title: '',
      certificateNumber: '',
      replace: false,
      notify: true,
    },
  })

  const revokeForm = useForm<RevokeFormValues>({
    defaultValues: {
      reason: '',
    },
  })

  const uploadCertificate = useUploadCertificate()
  const revokeCertificate = useRevokeCertificate()

  const filteredCertificates = useMemo(() => {
    return certificates.filter((certificate) => {
      if (typeFilter !== 'all' && certificate.type !== typeFilter) return false
      if (statusFilter === 'valid' && (certificate.isValid === false || certificate.isRevoked)) return false
      if (statusFilter === 'revoked' && !certificate.isRevoked) return false
      if (statusFilter === 'invalid' && certificate.isValid !== false) return false

      if (searchValue) {
        const userName = certificate.userId ? resolveUserName(certificate.userId, userMap) : ''
        const programmeTitle = certificate.programmeId ? programmeMap?.[certificate.programmeId]?.title ?? '' : ''
        const courseTitle = certificate.courseId ? courseMap?.[certificate.courseId]?.title ?? '' : ''
        const haystack = [
          certificate.title,
          certificate.certificateNumber,
          userName,
          programmeTitle,
          courseTitle,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return haystack.includes(searchValue)
      }

      return true
    })
  }, [certificates, typeFilter, statusFilter, searchValue, userMap, programmeMap, courseMap])

  const stats = useMemo(() => {
    const total = certificates.length
    const revoked = certificates.filter((item) => item.isRevoked).length
    const invalid = certificates.filter((item) => item.isValid === false && !item.isRevoked).length
    const active = total - revoked
    return { total, active, revoked, invalid }
  }, [certificates])

  const handleDownload = (certificate: CertificateModel) => {
    if (!certificate.downloadUrl) {
      toast.info('No download link is available for this certificate yet.')
      return
    }
    window.open(certificate.downloadUrl, '_blank', 'noopener,noreferrer')
  }

  const handleIssueSubmit = async (values: IssueCertificateFormValues) => {
    if (!uploadFile) {
      setFileError('Attach a certificate file before issuing.')
      return
    }
    setFileError(null)

    try {
      await uploadCertificate.mutateAsync({
        file: uploadFile,
        userId: values.userId.trim(),
        title: values.title.trim(),
        certificateNumber: values.certificateNumber.trim(),
        replace: values.replace,
        notify: values.notify,
      })
      toast.success('Certificate issued successfully')
      setIsIssueModalOpen(false)
      setUploadFile(null)
      issueForm.reset({
        userId: '',
        title: '',
        certificateNumber: '',
        replace: false,
        notify: true,
      })
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to issue certificate')
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setUploadFile(null)
      return
    }
    const validation = validateFileUpload(file)
    if (!validation.isValid) {
      setFileError(validation.error ?? 'Invalid file selected')
      setUploadFile(null)
      return
    }
    setFileError(null)
    setUploadFile(file)
  }

  const handleOpenRevoke = (certificate: CertificateModel) => {
    setRevokeTarget(certificate)
    revokeForm.reset({ reason: '' })
  }

  const handleRevokeSubmit = async (values: RevokeFormValues) => {
    if (!revokeTarget?.id) return
    try {
      await revokeCertificate.mutateAsync({
        certificateId: revokeTarget.id,
        payload: {
          reason: values.reason || 'Revoked by administrator',
        },
      })
      toast.success('Certificate revoked')
      setRevokeTarget(null)
      revokeForm.reset({ reason: '' })
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to revoke certificate')
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Certificates</h1>
          <p className="text-secondary-600">Issue, track, and revoke learner certificates.</p>
        </div>
        <Button onClick={() => setIsIssueModalOpen(true)}>
          <UploadCloud className="mr-2 h-4 w-4" />
          Issue certificate
        </Button>
      </header>

      <section className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Award} label="Total certificates" value={stats.total} tone="default" />
        <StatCard icon={ShieldCheck} label="Active" value={stats.active} tone="success" />
        <StatCard icon={ShieldOff} label="Revoked" value={stats.revoked} tone="warning" />
        <StatCard icon={AlertCircle} label="Invalid/expired" value={stats.invalid} tone="alert" />
      </section>

      <Card>
        <CardHeader className="flex-row gap-2 space-y-0 sm:flex sm:items-center sm:justify-between">
          <div>
            <CardTitle>Certificate directory</CardTitle>
            <CardDescription>Filter by learner, programme, or certificate number.</CardDescription>
          </div>
          {isFetching && (
            <div className="flex text-xs text-secondary-500">Refreshing…</div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-xl">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
              <Input
                placeholder="Search by name, certificate number, programme, or course"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value as CertificateTypeFilter)
                  setPage(1)
                }}
                className="rounded-md border border-secondary-200 px-3 py-2 text-sm"
              >
                <option value="all">All types</option>
                <option value="diploma">Diploma</option>
                <option value="programme">Programme</option>
                <option value="course">Course</option>
              </select>
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as CertificateStatusFilter)
                  setPage(1)
                }}
                className="rounded-md border border-secondary-200 px-3 py-2 text-sm"
              >
                <option value="all">All statuses</option>
                <option value="valid">Active</option>
                <option value="revoked">Revoked</option>
                <option value="invalid">Invalid</option>
              </select>
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner size="md" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-amber-800">
              <AlertCircle className="h-4 w-4" />
              <span>{(error as Error).message}</span>
            </div>
          )}

          {!isLoading && filteredCertificates.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-secondary-200 p-8 text-center">
              <Award className="h-10 w-10 text-secondary-300" />
              <h3 className="text-lg font-semibold text-secondary-900">No certificates found</h3>
              <p className="text-sm text-secondary-600">
                Try adjusting your search or filters, or issue a new certificate.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {filteredCertificates.map((certificate, index) => (
              <CertificateCard
                key={certificate.id ?? certificate.certificateNumber ?? `certificate-${index}`}
                certificate={certificate}
                userName={certificate.userId ? resolveUserName(certificate.userId, userMap) : '—'}
                programmeTitle={certificate.programmeId ? programmeMap?.[certificate.programmeId]?.title ?? '—' : '—'}
                courseTitle={certificate.courseId ? courseMap?.[certificate.courseId]?.title ?? '—' : '—'}
                onDownload={() => handleDownload(certificate)}
                onRevoke={() => handleOpenRevoke(certificate)}
                isRevoking={revokeCertificate.isPending && revokeTarget?.id === certificate.id}
              />
            ))}
          </div>

          {meta && filteredCertificates.length > 0 && (
            <Paginator
              meta={meta}
              page={page}
              perPage={perPage}
              onPageChange={(nextPage) => nextPage > 0 && setPage(nextPage)}
              onPerPageChange={(nextPerPage) => {
                setPage(1)
                setPerPage(nextPerPage)
              }}
              isPreviousData={isFetching}
            />
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isIssueModalOpen}
        onClose={() => {
          if (uploadCertificate.isPending) return
          setIsIssueModalOpen(false)
          setFileError(null)
          setUploadFile(null)
        }}
        title="Issue certificate"
        size="lg"
      >
        <form className="space-y-4" onSubmit={issueForm.handleSubmit(handleIssueSubmit)}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-800">Learner</label>
              <div className="relative">
                <select
                  className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  {...issueForm.register('userId', { required: 'Learner is required' })}
                  disabled={isLearnersLoading}
                  defaultValue=""
                >
                  <option value="" disabled>
                    {isLearnersLoading ? 'Loading learners…' : 'Select learner'}
                  </option>
                  {learnerOptions.map((learner) => (
                    <option key={learner.id ?? ''} value={learner.id ?? ''}>
                      {formatLearnerLabel(learner)}
                    </option>
                  ))}
                </select>
                {isLearnersLoading && (
                  <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
              </div>
              {issueForm.formState.errors.userId && (
                <p className="mt-1 text-xs text-red-600">{issueForm.formState.errors.userId.message}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-800">Certificate number</label>
              <Input
                placeholder="KBS-2025-000124"
                {...issueForm.register('certificateNumber', { required: 'Certificate number is required' })}
              />
              {issueForm.formState.errors.certificateNumber && (
                <p className="mt-1 text-xs text-red-600">{issueForm.formState.errors.certificateNumber.message}</p>
              )}
            </div>
          </div>


          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-800">Title</label>
            <Input
              placeholder="National Diploma Completion Certificate"
              {...issueForm.register('title', { required: 'Title is required' })}
            />
            {issueForm.formState.errors.title && (
              <p className="mt-1 text-xs text-red-600">{issueForm.formState.errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="mb-1 block text-sm font-medium text-secondary-800">Certificate file</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
              onChange={handleFileChange}
              className="block w-full text-sm text-secondary-800 file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary/90"
            />
      {uploadFile && (
        <p className="text-xs text-secondary-600">
          Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
        </p>
      )}
            {fileError && <p className="text-xs text-red-600">{fileError}</p>}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-secondary-800">
              <input type="checkbox" {...issueForm.register('replace')} className="h-4 w-4 rounded border-secondary-300" />
              Replace existing certificate
            </label>
            <label className="flex items-center gap-2 text-sm text-secondary-800">
              <input type="checkbox" {...issueForm.register('notify')} className="h-4 w-4 rounded border-secondary-300" />
              Notify learner by email
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (uploadCertificate.isPending) return
                setIsIssueModalOpen(false)
                setUploadFile(null)
                setFileError(null)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploadCertificate.isPending}>
              {uploadCertificate.isPending ? <LoadingSpinner size="sm" /> : <UploadCloud className="mr-2 h-4 w-4" />}
              {uploadCertificate.isPending ? 'Issuing…' : 'Issue certificate'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        title="Revoke certificate"
        size="md"
      >
        <form className="space-y-4" onSubmit={revokeForm.handleSubmit(handleRevokeSubmit)}>
          <p className="text-sm text-secondary-700">
            You are revoking certificate{' '}
            <span className="font-semibold text-secondary-900">
              {revokeTarget?.certificateNumber || revokeTarget?.title || 'this record'}
            </span>
            . This action cannot be undone.
          </p>

          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-800">Reason (optional)</label>
            <Textarea
              placeholder="Provide context for this revocation"
              {...revokeForm.register('reason')}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={revokeCertificate.isPending}>
              {revokeCertificate.isPending ? <LoadingSpinner size="sm" /> : <Ban className="mr-2 h-4 w-4" />}
              {revokeCertificate.isPending ? 'Revoking…' : 'Revoke'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function CertificateCard({
  certificate,
  userName,
  programmeTitle,
  courseTitle,
  onDownload,
  onRevoke,
  isRevoking,
}: {
  certificate: CertificateModel
  userName: string
  programmeTitle: string
  courseTitle: string
  onDownload: () => void
  onRevoke: () => void
  isRevoking: boolean
}) {
  const status = resolveStatusBadge(certificate)
  const typeLabel = certificate.type ? certificate.type.charAt(0).toUpperCase() + certificate.type.slice(1) : 'Certificate'

  return (
    <Card className="h-full">
      <CardHeader className="gap-2 space-y-1">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{certificate.title || 'Untitled certificate'}</CardTitle>
              <CardDescription>{typeLabel}</CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
            <Badge className={cn('text-xs font-medium', status.className)}>{status.label}</Badge>
            {certificate.certificateNumber && (
              <Badge variant="outline" className="text-xs">
                {certificate.certificateNumber}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-secondary-800">
        <InfoRow icon={<User className="h-4 w-4 text-secondary-500" />} label="Learner" value={userName || '—'} />
        <div className="grid gap-3 sm:grid-cols-2">
          <InfoRow
            icon={<FileText className="h-4 w-4 text-secondary-500" />}
            label="Programme"
            value={programmeTitle || '—'}
          />
          <InfoRow
            icon={<FileText className="h-4 w-4 text-secondary-500" />}
            label="Course"
            value={courseTitle || '—'}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <InfoRow
            icon={<Calendar className="h-4 w-4 text-secondary-500" />}
            label="Issued at"
            value={formatDate(certificate.issuedAt ?? certificate.createdAt)}
          />
          <InfoRow
            icon={certificate.isValid === false ? <ShieldOff className="h-4 w-4 text-amber-600" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
            label="Validity"
            value={certificate.isRevoked ? 'Revoked' : certificate.isValid === false ? 'Invalid' : 'Valid'}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDownload}
            disabled={!certificate.downloadUrl}
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onRevoke}
            disabled={certificate.isRevoked || isRevoking}
          >
            {isRevoking ? <LoadingSpinner size="sm" /> : <Ban className="mr-2 h-4 w-4" />}
            {certificate.isRevoked ? 'Revoked' : 'Revoke'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  value: number
  tone?: 'default' | 'success' | 'warning' | 'alert'
}) {
  const toneClass: Record<'default' | 'success' | 'warning' | 'alert', string> = {
    default: 'bg-secondary-50 text-secondary-900',
    success: 'bg-emerald-50 text-emerald-800',
    warning: 'bg-amber-50 text-amber-800',
    alert: 'bg-rose-50 text-rose-800',
  }

  return (
    <div className={cn('rounded-lg border border-secondary-200 p-4', toneClass[tone])}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-white/70 p-2 text-secondary-700">
            <Icon className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className="text-xl font-semibold">{value}</span>
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <div>
        <div className="text-xs uppercase tracking-wide text-secondary-500">{label}</div>
        <div className="text-sm font-medium text-secondary-900">{value || '—'}</div>
      </div>
    </div>
  )
}

function resolveUserName(userId: string, userMap?: Record<string, UserModel>) {
  if (!userId || !userMap) return ''
  const user = userMap[userId]
  if (!user) return userId
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return fullName || userId
}

function formatLearnerLabel(user: UserModel) {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  const primary = fullName || user.email || user.id || 'Unnamed learner'
  const secondary = user.email && fullName ? ` • ${user.email}` : ''
  return `${primary}${secondary}`
}
